import { Badge } from "@/components/Badge";
import { Button } from "@/components/Button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/Card";
import { Clock, Gift, ExternalLink, X, CheckCircle } from "lucide-react";
import { UserAvatar } from "@/components/UserAvatar";
import { cn } from "@/lib/utils";
import { router } from '@inertiajs/react';
import { useState } from 'react';
import { ConfirmDialog } from "@/components/ConfirmDialog";

export function RedemptionsTab({ redemptions = [], stats = { sent: 0, fulfilled: 0 } }) {
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, type: null, redemption: null, selectedIds: [] });
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'sent', 'fulfilled'
  const [selectedRedemptions, setSelectedRedemptions] = useState([]);

  const handleCancel = (redemption) => {
    setConfirmDialog({ isOpen: true, type: 'cancel', redemption, selectedIds: [] });
  };

  const handleBulkFulfill = () => {
    if (selectedRedemptions.length === 0) return;
    setConfirmDialog({ isOpen: true, type: 'bulk_fulfill', redemption: null, selectedIds: selectedRedemptions });
  };

  const executeCancel = () => {
    router.post(`/admin/redemptions/${confirmDialog.redemption.id}/cancel`, {}, {
      preserveScroll: false,
      onSuccess: () => closeDialog()
    });
  };

  const executeBulkFulfill = () => {
    router.post('/admin/redemptions/bulk_fulfill', {
      redemption_ids: confirmDialog.selectedIds
    }, {
      preserveScroll: false,
      onSuccess: () => {
        setSelectedRedemptions([]);
        closeDialog();
      }
    });
  };

  const closeDialog = () => {
    setConfirmDialog({ isOpen: false, type: null, redemption: null, selectedIds: [] });
  };

  const toggleSelection = (redemptionId) => {
    setSelectedRedemptions(prev =>
      prev.includes(redemptionId)
        ? prev.filter(id => id !== redemptionId)
        : [...prev, redemptionId]
    );
  };

  const toggleSelectAll = () => {
    const sentRedemptions = filteredRedemptions.filter(r => r.status === 'sent');
    if (selectedRedemptions.length === sentRedemptions.length && sentRedemptions.length > 0) {
      setSelectedRedemptions([]);
    } else {
      setSelectedRedemptions(sentRedemptions.map(r => r.id));
    }
  };

  const openGiftCardUrl = (redemptionId) => {
    const url = `${window.location.origin}/redemptions/${redemptionId}/gift_card`;
    window.open(url, '_blank');
  };

  const getCategoryBadge = (category) => {
    const configs = {
      cash: { label: '💰 Saldo', className: 'bg-green-100 text-green-700' },
      experiences: { label: '🎉 Experiencias', className: 'bg-amber-100 text-amber-700' },
      charity: { label: '❤️ Caridad', className: 'bg-red-100 text-red-700' }
    };
    const config = configs[category] || { label: category, className: 'bg-gray-100 text-gray-700' };
    return <Badge className={cn("text-[10px]", config.className)}>{config.label}</Badge>;
  };

  const getStatusBadge = (status) => {
    const configs = {
      sent: { label: 'Solicitud Pendiente', className: 'bg-orange-100 text-orange-700' },
      fulfilled: { label: 'Cumplida', className: 'bg-blue-100 text-blue-700' },
      cancelled: { label: 'Cancelada', className: 'bg-gray-100 text-gray-700' }
    };
    const config = configs[status] || { label: status, className: 'bg-gray-100 text-gray-700' };
    return <Badge className={cn("text-[10px]", config.className)}>{config.label}</Badge>;
  };

  // Filtrar redemptions según el filtro seleccionado
  const filteredRedemptions = redemptions.filter(redemption => {
    if (statusFilter === 'all') return true;
    return redemption.status === statusFilter;
  });

  return (
    <>
      <Card className="border-t-4 border-t-orange-500 shadow-sm animate-in fade-in slide-in-from-bottom-2">
        <CardHeader>
          <CardTitle className="flex items-center text-xl">
            <CheckCircle className="w-5 h-5 mr-2 text-orange-500" />
            Validación de Canjes
          </CardTitle>
          <p className="text-sm text-gray-500">Revisa y aprueba las solicitudes de canje de puntos.</p>
        </CardHeader>
        <CardContent>
        {/* Stats Cards - Filtros */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <button
            onClick={() => setStatusFilter('all')}
            className={cn(
              "rounded-xl p-4 transition-all cursor-pointer hover:shadow-md",
              statusFilter === 'all'
                ? "bg-gray-100 border-2 border-gray-400 shadow-md"
                : "bg-gray-50 border border-gray-200 hover:border-gray-300"
            )}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-600 uppercase">Todas</p>
                <h3 className="text-2xl font-bold text-gray-700">{redemptions.length}</h3>
              </div>
              <CheckCircle className="w-8 h-8 text-gray-300" />
            </div>
          </button>

          <button
            onClick={() => setStatusFilter('sent')}
            className={cn(
              "rounded-xl p-4 transition-all cursor-pointer hover:shadow-md",
              statusFilter === 'sent'
                ? "bg-orange-100 border-2 border-orange-400 shadow-md"
                : "bg-orange-50 border border-orange-100 hover:border-orange-200"
            )}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-orange-600 uppercase">Solicitudes Pendientes</p>
                <h3 className="text-2xl font-bold text-orange-700">{stats.sent}</h3>
              </div>
              <Clock className="w-8 h-8 text-orange-200" />
            </div>
          </button>

          <button
            onClick={() => setStatusFilter('fulfilled')}
            className={cn(
              "rounded-xl p-4 transition-all cursor-pointer hover:shadow-md",
              statusFilter === 'fulfilled'
                ? "bg-blue-100 border-2 border-blue-400 shadow-md"
                : "bg-blue-50 border border-blue-100 hover:border-blue-200"
            )}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-blue-600 uppercase">Cumplidas</p>
                <h3 className="text-2xl font-bold text-blue-700">{stats.fulfilled}</h3>
              </div>
              <Gift className="w-8 h-8 text-blue-200" />
            </div>
          </button>
        </div>

        {/* Bulk Actions */}
        {selectedRedemptions.length > 0 && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center justify-between">
            <span className="text-sm font-medium text-green-800">
              {selectedRedemptions.length} solicitud{selectedRedemptions.length !== 1 ? 'es' : ''} seleccionada{selectedRedemptions.length !== 1 ? 's' : ''}
            </span>
            <Button
              onClick={handleBulkFulfill}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              Redimir Seleccionados
            </Button>
          </div>
        )}

        {/* Redemptions Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="bg-gray-50 text-gray-500 font-bold uppercase text-xs tracking-wider">
              <tr>
                <th className="px-4 py-3 w-12">
                  <input
                    type="checkbox"
                    checked={selectedRedemptions.length > 0 && selectedRedemptions.length === filteredRedemptions.filter(r => r.status === 'sent').length}
                    onChange={toggleSelectAll}
                    className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                    disabled={filteredRedemptions.filter(r => r.status === 'sent').length === 0}
                  />
                </th>
                <th className="px-4 py-3">Fecha</th>
                <th className="px-4 py-3">Usuario</th>
                <th className="px-4 py-3">Categoría</th>
                <th className="px-4 py-3">Detalles</th>
                <th className="px-4 py-3 text-right">Puntos</th>
                <th className="px-4 py-3 text-center">Estado</th>
                <th className="px-4 py-3 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {filteredRedemptions.length === 0 ? (
                <tr><td colSpan="8" className="text-center py-8 text-gray-400">
                  {statusFilter === 'all'
                    ? 'No hay solicitudes de canje.'
                    : `No hay solicitudes ${statusFilter === 'sent' ? 'pendientes' : 'cumplidas'}.`}
                </td></tr>
              ) : (
                filteredRedemptions.map((redemption) => (
                  <tr key={redemption.id} className="hover:bg-gray-50/50 group">
                    <td className="px-4 py-3">
                      {redemption.status === 'sent' && (
                        <input
                          type="checkbox"
                          checked={selectedRedemptions.includes(redemption.id)}
                          onChange={() => toggleSelection(redemption.id)}
                          className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                        />
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-xs text-gray-600">
                        {new Date(redemption.created_at).toLocaleDateString()}
                      </div>
                      <div className="text-[10px] text-gray-400">
                        {new Date(redemption.created_at).toLocaleTimeString()}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <UserAvatar user={redemption.user} size={32} />
                        <div>
                          <div className="font-medium text-gray-800 text-xs">{redemption.user?.full_name}</div>
                          <div className="text-[10px] text-gray-400">{redemption.user?.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {getCategoryBadge(redemption.category)}
                    </td>
                    <td className="px-4 py-3 max-w-xs">
                      <p className="text-xs text-gray-600 truncate" title={redemption.fulfillment_details}>
                        {redemption.fulfillment_details || 'Sin detalles'}
                      </p>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="font-mono font-bold text-orange-600 text-xs">
                        {redemption.points_spent} pts
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {getStatusBadge(redemption.status)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        {redemption.status === 'sent' && (
                          <>
                            <Button
                              size="sm"
                              className="h-7 w-7 p-0 bg-blue-600 hover:bg-blue-700 text-white"
                              onClick={() => openGiftCardUrl(redemption.id)}
                              title="Abrir URL de Gift Card"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 w-7 p-0 text-red-600 hover:bg-red-50"
                              onClick={() => handleCancel(redemption)}
                              title="Cancelar"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        {(redemption.status === 'fulfilled' || redemption.status === 'cancelled') && (
                          <div className="text-[10px] text-gray-400 text-right">
                            {redemption.fulfilled_by_user && (
                              <div>Por: {redemption.fulfilled_by_user.full_name}</div>
                            )}
                            {redemption.fulfilled_at && (
                              <div>{new Date(redemption.fulfilled_at).toLocaleDateString()}</div>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>

    {/* Cancel Dialog */}
    {confirmDialog.type === 'cancel' && (
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={closeDialog}
        onConfirm={executeCancel}
        title="Cancelar Solicitud de Canje"
        description={`¿Estás seguro de cancelar la solicitud de ${confirmDialog.redemption?.user?.full_name} por ${confirmDialog.redemption?.points_spent} puntos?`}
        confirmText="Cancelar Canje"
        cancelText="Volver"
        variant="warning"
      />
    )}

    {/* Bulk Fulfill Dialog */}
    {confirmDialog.type === 'bulk_fulfill' && (
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={closeDialog}
        onConfirm={executeBulkFulfill}
        title="Redimir Solicitudes Seleccionadas"
        description={`¿Estás seguro de marcar ${confirmDialog.selectedIds.length} solicitud${confirmDialog.selectedIds.length !== 1 ? 'es' : ''} como redimida${confirmDialog.selectedIds.length !== 1 ? 's' : ''}? Los usuarios serán notificados automáticamente.`}
        confirmText="Redimir Todas"
        cancelText="Cancelar"
        variant="success"
      />
    )}
    </>
  );
}
